import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "skl_cookie_consent_v1";

type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: 1;
};

function saveConsent(c: Omit<Consent, "necessary" | "timestamp" | "version">) {
  const payload: Consent = {
    necessary: true,
    analytics: c.analytics,
    marketing: c.marketing,
    timestamp: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent("skl:cookie-consent", { detail: payload }));
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const saveChoices = () => {
    saveConsent({ analytics, marketing });
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-samtykke"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-foreground/10 bg-card/95 p-5 shadow-2xl backdrop-blur-md sm:p-6">
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Vi respekterer dit privatliv
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Vi anvender kun cookies, der er strengt nødvendige for, at hjemmesiden fungerer (fx
              login og sikkerhed). Vi vil gerne bede om dit samtykke, hvis vi senere ønsker at
              anvende statistik- eller marketingcookies. Du kan altid ændre eller trække dit
              samtykke tilbage. Læs mere i vores{" "}
              <Link to="/cookiepolitik" className="underline hover:no-underline">
                cookiepolitik
              </Link>{" "}
              og{" "}
              <Link to="/privatlivspolitik" className="underline hover:no-underline">
                privatlivspolitik
              </Link>
              .
            </p>
          </div>

          {showSettings && (
            <div className="space-y-3 rounded-lg border border-foreground/10 bg-background/60 p-4">
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium text-foreground">
                    Strengt nødvendige
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Kræves for basal funktionalitet. Kan ikke fravælges.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="mt-1 h-4 w-4 accent-primary"
                  aria-label="Strengt nødvendige cookies (altid aktive)"
                />
              </label>
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium text-foreground">Statistik</span>
                  <span className="block text-xs text-muted-foreground">
                    Anonym måling af brug, så vi kan forbedre siden.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primary"
                  aria-label="Statistikcookies"
                />
              </label>
              <label className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-medium text-foreground">Marketing</span>
                  <span className="block text-xs text-muted-foreground">
                    Bruges til at vise relevant indhold og annoncer.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primary"
                  aria-label="Marketingcookies"
                />
              </label>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            {!showSettings ? (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Tilpas
              </button>
            ) : (
              <button
                type="button"
                onClick={saveChoices}
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Gem valg
              </button>
            )}
            <button
              type="button"
              onClick={rejectAll}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              Afvis alle
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Accepter alle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
